import React, { useState } from "react";
import withProviders from "../../hooks/withProviders";
import PersonalMessages from "../../features/personalmessages";
import {MessageThreadModal} from "../../features/messagethread";
import CreatePersonalMessageModal from "../../features/createpersonalmessagemodal";
import MessageManagementProvider, { MessageManagementDefaultFilter } from "../../hooks/MessageManagementContext";
import { FilterProvider, useFilterContext } from "../../hooks/FilterContext";
import { ThemedIcon, Main, PageSubHeader, FiltersAppliedWarningMessage } from "../../components/utilities";
import PageHeader, { PageHeaderTop } from "../../components/layout/PageHeader";
import { PageHeaderTitle } from "../../components/utilities/PageHeader";
import { IconButton } from "../../components";
import { FormInputWithSearchIcon } from "../../components/forms";
import { useMessageManagementContext } from "../../hooks/MessageManagementContext";
import { CreatePersonalMessageButton } from "../../features/createpersonalmessagemodal";
import { useAuth } from "react-oidc-context";
import { toNString } from "../../helpers/guids";
import { openMessageFiltersModal } from "../../components/modals/message-filter";
import { PersonalMessageTreadControllerProvider, usePersonalMessageThreadController } from "../../features/personalmessages/hook/PersonalMessageThreadControllerContext";

// TODO: convert these modals into confirmable modals to fit with new method for dealing with

// This is the internal child view of the actual page and holds the full layout and accesses the management
// context, which is what runs the main query.
const PersonalMessagesView = () => {
    const { clearFilter, defaultFilters, filter, isFiltersApplied, setFilter } = useFilterContext();
    const { searchTerm, setSearchTerm } = useMessageManagementContext();
    const { threadId, setThread, userId } = usePersonalMessageThreadController();
    const [showCreateMessageModal, setShowCreateMessageModal] = React.useState(false);

    const onCreateMessage = () => setShowCreateMessageModal(() => true);

    const onOpenMessageFilters = () => {
        openMessageFiltersModal('Adviser', filter, defaultFilters, searchTerm).then(
            form => {
                const { searchTerm, ...rest } = form;
                setFilter(() => ({ ...rest }));       
            },
            () => { }
        );
    };
    
    return (
        <React.Fragment>
            <CreatePersonalMessageModal
                show={showCreateMessageModal}
                onHide={() => setShowCreateMessageModal(false)}
            />
            <MessageThreadModal
                handleClose={() => setThread(null)}
                show={threadId !== null}
                threadId={threadId}
                userId={userId}
            />
            <PageHeader>
                <PageHeaderTop>
                    <PageHeaderTitle>
                        <span>Messages, Tasks and Notifications</span>
                    </PageHeaderTitle>
                </PageHeaderTop>
            </PageHeader>
            <Main className="d-flex flex-column">
                <PageSubHeader>
                    <div className="d-flex justify-content-between align-items-center">
                        <div className="d-flex align-items-center">
                            {isFiltersApplied && (
                                <React.Fragment>
                                    <ThemedIcon icon="fa-triangle-exclamation" size="2x" variant="warning" />
                                    <FiltersAppliedWarningMessage className="ms-2 text-muted">
                                        Warning - Filters are currently being applied to your results.
                                        <br />
                                        <a href="#" onClick={clearFilter}>Click here</a> to clear your filters.
                                    </FiltersAppliedWarningMessage>
                                </React.Fragment>
                            )}
                        </div>
                        <div className="d-flex flex-fill justify-content-end align-items-center gap-2">
                            <FormInputWithSearchIcon
                                className="w-25"
                                placeholder="Search messages..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(() => e.target.value)}
                            />
                            <IconButton icon={['fas', 'filter']} onClick={onOpenMessageFilters}>
                                Filter Messages
                            </IconButton>
                            <CreatePersonalMessageButton onClick={onCreateMessage} />
                        </div>
                    </div>
                </PageSubHeader>
                <section className="px-4 my-3 h-100">
                    <PersonalMessages />
                </section>
            </Main>
        </React.Fragment>
    );
};

// We need to render the page parent FIRST because it sets up the message provider that runs the main query to
// get the messages to display. React is weird and the provider has to be done in the parent component for the useContext
// hook to work in the direct child.
const PersonalMessagesPage = () => {
    const { filter, isReady } = useFilterContext();
    const { user } = useAuth();
    const currentUserId = user?.profile.sub ? toNString(user.profile.sub) : null;

    // do not render anything if there is no default current user as we need it as part of the search url
    if (!currentUserId) {
        return null;
    }

    return (
        <MessageManagementProvider userId={currentUserId} filter={filter} isReady={isReady}>
            <PersonalMessageTreadControllerProvider>
                <PersonalMessagesView />
            </PersonalMessageTreadControllerProvider>
        </MessageManagementProvider>
    );
};

// this builds a custom filter provider that will use reacts tree to access the useAuth hook to grab our current user-id
// then we can setup the default filters directly before rendering the rest of the tree. This will also force whenever
// the clear-filters button is clicked to essentially put the user-id back in as the default recipient of the messages.. cool -huh?
export default withProviders(({ children }) => {
    const { user } = useAuth();
    const currentUserId = user?.profile.sub ? toNString(user.profile.sub) : null;
    const [defaultFilters, setDefaultFilters] = React.useState({ ...MessageManagementDefaultFilter, recipientId: currentUserId });

    return (
        <FilterProvider defaultFilters={defaultFilters}>
            {children}
        </FilterProvider>
    );
})(PersonalMessagesPage);